import { ChevronLeft } from 'lucide-react';
import { format } from 'date-fns';
import { he } from 'date-fns/locale';

import { cn } from '../../../lib/cn';
import type { ProjectRow, ProjectStatus } from './projects.schema';
import styles from './projects.module.css';

const STATUS_LABELS: Record<ProjectStatus, string> = {
  quote: 'הצעת מחיר',
  active: 'פעיל',
  paused: 'בהמתנה',
  completed: 'הושלם',
  cancelled: 'בוטל',
};

const STATUS_CLASS: Record<ProjectStatus, string> = {
  quote: 'statusQuote',
  active: 'statusActive',
  paused: 'statusPaused',
  completed: 'statusCompleted',
  cancelled: 'statusCancelled',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  try {
    return format(new Date(iso), 'dd/MM/yy', { locale: he });
  } catch {
    return iso;
  }
}

interface ProjectListProps {
  projects: ProjectRow[];
  companyNameById: Record<string, string>;
  onSelect: (project: ProjectRow) => void;
}

export function ProjectList({ projects, companyNameById, onSelect }: ProjectListProps) {
  return (
    <div className={styles.table} role="table" aria-label="פרויקטים">
      <div className={cn(styles.rowGrid, styles.head)} role="row">
        <div role="columnheader">פרויקט</div>
        <div role="columnheader">לקוח</div>
        <div role="columnheader">סטטוס</div>
        <div role="columnheader">תאריך סיום</div>
        <div role="columnheader" />
      </div>

      {projects.map((project) => {
        const companyName = project.company_id ? (companyNameById[project.company_id] ?? '—') : '—';
        const statusKey = STATUS_CLASS[project.status];

        return (
          <div
            key={project.id}
            className={styles.rowGrid}
            role="row"
            tabIndex={0}
            onClick={() => {
              onSelect(project);
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onSelect(project);
              }
            }}
          >
            <span className={styles.projectCell}>
              <strong>{project.name}</strong>
              {project.expected_end_date ? <small>סיום צפוי {formatDate(project.expected_end_date)}</small> : null}
            </span>
            <span>{companyName}</span>
            <span className={cn(styles.statusBadge, styles[statusKey])}>{STATUS_LABELS[project.status]}</span>
            <span className={styles.dateCell}>
              <span className="num">{formatDate(project.completed_at ?? project.expected_end_date)}</span>
            </span>
            <ChevronLeft size={16} className={styles.chevron} aria-hidden="true" />
          </div>
        );
      })}
    </div>
  );
}
